export const FIELD_LABEL_SELECTOR_PREFIX = '--field-';

interface FieldLabelOptions {
  includeCssFallback?: boolean;
}

export const defaultSimFieldLabels: Record<string, string> = {
  'sim.status': 'Status',
  'sim.legacyStatus': 'Legacy Status',
  'sim.serialNumber': 'Serial No.',
  'sim.prefix': 'Prefix',
  'sim.tel': 'Tel No.',
  'sim.network': 'Network',
  'sim.accountId': 'Account',
  'sim.accountTitle': 'Account Title',
  'sim.raNumber': 'RA No.',
  'sim.tariff': 'Tariff',
  'sim.commencementDate': 'Commencement',
  'sim.provisioningOrder': 'Provisioning Order',
  'sim.order1': 'Order 1',
  'sim.order2': 'Order 2',
  'sim.order3': 'Order 3',
  'sim.order4': 'Order 4',
  'sim.order5': 'Order 5',
  'sim.simType': 'SIM Type',
  'sim.dataBundleMb': 'Tariff Bundle',
  'sim.dataUsageMb': 'Data Usage',
  'sim.voiceMinutes': 'Voice Mins',
  'sim.smsCount': 'Total SMS',
  'sim.activeBundle': 'Active Bundle',
  'sim.operationInProgress': 'Operation In Progress',
  'sim.iccid': 'Iccid',
  'sim.privateIp': 'Private IP',
  'sim.publicIp': 'Public IP',
  'sim.imei': 'IMEI',
  'sim.alertManagerEnabled': 'Alert Manager Enabled',
  'sim.subportal': 'Subportal',
  'sim.inSession': 'In Session'
};

const simFieldAliases: Record<string, string> = {
  serialNumber: 'serialNumber',
  serialNo: 'serialNumber',
  stockSerialNo: 'serialNumber',
  prefix: 'prefix',
  tel: 'tel',
  telNumber: 'tel',
  msisdn: 'tel',
  status: 'status',
  statusName: 'status',
  legacyStatus: 'legacyStatus',
  legacyStatusName: 'legacyStatus',
  network: 'network',
  accountId: 'accountId',
  hiremanAccountId: 'accountId',
  accountTitle: 'accountTitle',
  raNumber: 'raNumber',
  tariff: 'tariff',
  tariffName: 'tariff',
  commencementDate: 'commencementDate',
  provisioningOrder: 'provisioningOrder',
  order1: 'order1',
  order2: 'order2',
  order3: 'order3',
  order4: 'order4',
  order5: 'order5',
  simType: 'simType',
  dataBundleMb: 'dataBundleMb',
  dataUsageMb: 'dataUsageMb',
  voiceMinutes: 'voiceMinutes',
  smsCount: 'smsCount',
  activeBundle: 'activeBundle',
  operationInProgress: 'operationInProgress',
  iccid: 'iccid',
  privateIp: 'privateIp',
  publicIp: 'publicIp',
  imei: 'imei',
  alertManagerEnabled: 'alertManagerEnabled',
  subportal: 'subportal',
  inSession: 'inSession'
};

export function getFieldLabel(
  fieldKey: string,
  explicitLabels?: Record<string, string>,
  defaultLabels?: Record<string, string>,
  options?: FieldLabelOptions
): string {
  const explicitLabel = explicitLabels?.[fieldKey]?.trim();
  if (explicitLabel) {
    return explicitLabel;
  }

  const selector = getFieldLabelSelector(fieldKey);
  if (options?.includeCssFallback !== false && selector && typeof window !== 'undefined') {
    const cssValue = window.getComputedStyle(document.documentElement).getPropertyValue(selector).trim();
    if (cssValue) {
      return cssValue;
    }
  }

  return defaultLabels?.[fieldKey] || defaultSimFieldLabels[fieldKey] || fieldKey;
}

export function getSimFieldLabel(
  fieldName: string,
  explicitLabels?: Record<string, string>,
  defaultLabels?: Record<string, string>,
  options?: FieldLabelOptions
): string {
  return getFieldLabel(`sim.${fieldName}`, explicitLabels, defaultLabels, options);
}

export function getSimFieldLabelForAlias(alias: string, fallback?: string): string {
  const fieldName = simFieldAliases[alias];
  if (fieldName) {
    return getSimFieldLabel(fieldName);
  }

  return fallback ?? alias;
}

export function getFieldLabelSelector(fieldKey: string): string {
  return `${FIELD_LABEL_SELECTOR_PREFIX}${fieldKey
    .replace('.', '-')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()}`;
}

export function applyFieldLabels(fieldLabels: Record<string, string>, supportedFieldKeys?: string[]) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const fieldKeys = supportedFieldKeys || Array.from(new Set([...Object.keys(defaultSimFieldLabels), ...Object.keys(fieldLabels)]));

  fieldKeys.forEach((fieldKey) => {
    const selector = getFieldLabelSelector(fieldKey);
    const label = fieldLabels[fieldKey]?.trim();

    if (label) {
      root.style.setProperty(selector, label);
      return;
    }

    root.style.removeProperty(selector);
  });
}

export function normalizeSpreadsheetHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function getResolvedFieldLabels(
  fieldKeys: string[],
  explicitLabels?: Record<string, string>,
  defaultLabels?: Record<string, string>,
  options?: FieldLabelOptions
): Record<string, string> {
  return fieldKeys.reduce<Record<string, string>>((labels, fieldKey) => {
    labels[fieldKey] = getFieldLabel(fieldKey, explicitLabels, defaultLabels, options);
    return labels;
  }, {});
}

export function findDuplicateFieldLabels(
  fieldKeys: string[],
  explicitLabels?: Record<string, string>,
  defaultLabels?: Record<string, string>,
  options?: FieldLabelOptions
): string[] {
  const resolvedLabels = getResolvedFieldLabels(fieldKeys, explicitLabels, defaultLabels, options);
  const keysByNormalizedLabel = Object.entries(resolvedLabels).reduce<Record<string, string[]>>((acc, [fieldKey, label]) => {
    const normalizedLabel = normalizeSpreadsheetHeader(label);

    if (!normalizedLabel) {
      return acc;
    }

    acc[normalizedLabel] = [...(acc[normalizedLabel] || []), fieldKey];
    return acc;
  }, {});

  return Object.values(keysByNormalizedLabel)
    .filter((fieldNames) => fieldNames.length > 1)
    .map((fieldNames) => resolvedLabels[fieldNames[0]]);
}

export function getUniqueResolvedFieldLabels(
  fieldKeys: string[],
  explicitLabels?: Record<string, string>,
  defaultLabels?: Record<string, string>
): Record<string, string> {
  const resolvedLabels = getResolvedFieldLabels(fieldKeys, explicitLabels, defaultLabels);
  const occurrencesByNormalizedLabel: Record<string, number> = {};

  return fieldKeys.reduce<Record<string, string>>((labels, fieldKey) => {
    const label = resolvedLabels[fieldKey];
    const normalizedLabel = normalizeSpreadsheetHeader(label);
    const occurrence = (occurrencesByNormalizedLabel[normalizedLabel] || 0) + 1;
    const defaultLabel = defaultLabels?.[fieldKey] || defaultSimFieldLabels[fieldKey] || fieldKey;

    occurrencesByNormalizedLabel[normalizedLabel] = occurrence;
    labels[fieldKey] = occurrence === 1 ? label : `${label} (${defaultLabel})`;
    return labels;
  }, {});
}

export function getOrderFieldSpreadsheetKeys(
  fieldName: 'order1' | 'order2' | 'order3' | 'order4' | 'order5',
  explicitLabels?: Record<string, string>,
  defaultLabels?: Record<string, string>
): string[] {
  return Array.from(
    new Set([
      normalizeSpreadsheetHeader(fieldName),
      normalizeSpreadsheetHeader(getSimFieldLabel(fieldName, undefined, defaultLabels)),
      normalizeSpreadsheetHeader(getSimFieldLabel(fieldName, explicitLabels, defaultLabels))
    ])
  );
}
